import { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, Send, Check, Trash2, Pencil, Receipt, FileDown, FolderDown, X, Mail, Copy, AlertTriangle, Ban } from 'lucide-react';
import { fatture as fattureApi, clienti as clientiApi, cantieri as cantieriApi } from '../utils/api';
import { copiaTesto } from '../utils/clipboard';
import { useAuth } from '../context/AuthContext';
import { InvioClienteModal, InvioClientiModal } from '../components/invio/InvioDocumentoModals';
import EmissioneFIC from '../components/fic/EmissioneFIC';
import { etichettaListaFIC } from './ficEsiti';
import { STATI_ANNULLABILI, stileAnnullata, testoAvvisoAnnulla } from './fattureAnnulla';
import { mancantiPerEmettere, testoCosaManca } from './fattureEmissione';
import { importiAnteprimaModifica, messaggioErroreSalvataggio, moduloIniziale, opzioniAliquotaFattura, senzaImportiInvariati } from './fattureImporti';
import useIsMobile from '../hooks/useIsMobile';
import { useListMode, ListModeToggle } from '../components/shared/ListView';
import DataCard from '../components/shared/DataCard';
import RankingTable from '../components/shared/RankingTable';

// Config invio documento al cliente (modali condivise) — vedi InvioDocumentoModals.jsx
const FATTURA_INVIO_CONFIG = {
  oggettoLabel: 'Proforma',
  filePrefix: 'Proforma',
  idsKey: 'fattura_ids',
  emailCliente: (d) => d.cliente_email || '',
  nomeCliente: (d) => d.cliente_nome || d.cliente_nome_snapshot || 'il cliente',
  importo: (d) => d.totale,
  messaggioSingolo: (d) => {
    const rif = d.cantiere_titolo || d.descrizione || '';
    // Guardia numero: bozza numberless (post Commit 0) → niente "N° null" al cliente.
    const num = d.numero ? ` N° ${d.numero}` : '';
    return `in allegato trova la proforma${num}${rif ? ` relativa a ${rif}` : ''}. Per qualsiasi chiarimento resto a disposizione.`;
  },
  messaggioMultiplo: 'in allegato trova la proforma. Per qualsiasi chiarimento resto a disposizione.',
  singoloOkText: (dest) => `Proforma inviata a ${dest}`,
  multiOkText: (n) => `${n} ${n === 1 ? 'proforma inviata' : 'proforma inviate'} ai clienti`,
  inviaCliente: fattureApi.inviaCliente,
  inviaClienti: fattureApi.inviaClienti,
};

const fmtEur = (n) => n == null ? '—' : `€ ${Number(n).toLocaleString('it-IT', { minimumFractionDigits: 2 })}`;
const fmtData = (d) => { if (!d) return '—'; const [y, m, g] = d.split('-'); return `${g}/${m}/${y}`; };
// Testo di sollecito predefinito quando non c'e' gia' una bozza (es. preparata
// a voce): compilato col cliente/importo/scadenza, editabile prima dell'invio.
// Concatenazione, mai template annidati (regola 6).
const bozzaSollecitoDefault = (f) => {
  const cli = f.cliente_nome || f.cliente_nome_snapshot || 'Cliente';
  const rif = f.numero ? `n. ${f.numero} ` : '';
  const conScad = f.data_scadenza ? `, con scadenza ${fmtData(f.data_scadenza)},` : '';
  return `Gentile ${cli},\n\n`
    + `le ricordiamo gentilmente che la fattura ${rif}dell'importo di ${fmtEur(f.totale)}${conScad} risulta ancora in attesa di pagamento.\n\n`
    + `Le chiediamo cortesemente di provvedere al saldo. Restiamo a disposizione per qualsiasi necessità.\n\n`
    + `Cordiali saluti`;
};
// GATE C.1 — formattazione di un DATETIME ISO. fmtData sopra vale solo per le
// DATE (`YYYY-MM-DD`): passargli un datetime produrrebbe "31T10:22:33Z/07/2026",
// cioe' una data sbagliata mostrata con sicurezza. Due funzioni, due tipi.
const fmtQuando = (iso, { conOra = false } = {}) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const data = d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
  if (!conOra) return data;
  return `${data} alle ${d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
};

// Commit 0 fiscale — la bozza non ha numero (assegnato all'invio): etichetta chiara.
const NUM_BOZZA = "Bozza · n. all'invio";

// Helper visivi condivisi (design system)
const CARD_SHADOW = '0 6px 18px rgba(35,38,47,0.08)';
const ROW_BG = (i) => (i % 2 === 0 ? 'var(--ds-bg)' : 'var(--chip)');
const ROW_HOVER = 'color-mix(in srgb, var(--brand) 9%, var(--ds-bg))';
const tint = (c, pct) => `color-mix(in srgb, ${c} ${pct}%, transparent)`;

// Stato fattura → token semantico (bg via color-mix)
const STATO_META = {
  bozza:    { label: 'Bozza',     color: 'var(--ds-muted)' },
  inviata:  { label: 'Inviata al cliente', color: 'var(--warn)' },
  pagata:   { label: 'Pagata',    color: 'var(--ok)' },
  scaduta:  { label: 'Scaduta',   color: 'var(--ds-danger)' },
  annullata:{ label: 'Annullata', color: 'var(--ds-muted)' },
};
const TIPO_LABEL = {
  standard:          'Standard',
  acconto:           'Acconto',
  saldo:             'Saldo finale',
  stato_avanzamento: 'SAL',
};
// Guida sia le <option> del form sia l'etichetta in lettura: una voce tolta qui
// sparisce da entrambi. Deve restare allineata a Fattura.METODI_PAGAMENTO.
const METODI_LABEL = {
  bonifico: 'Bonifico', contanti: 'Contanti',
  carta: 'Carta', assegno: 'Assegno', riba: 'Ri.Ba.',
};
const STATI_LIST = ['bozza', 'inviata', 'pagata', 'scaduta', 'annullata'];

const FORM_EMPTY = {
  cliente: '', cantiere: '', tipo: 'standard', descrizione: '',
  imponibile: '', aliquota_iva: 22,
  data_emissione: new Date().toISOString().split('T')[0],
  data_scadenza: '', metodo_pagamento: 'bonifico',
  note_cliente: '', note_interne: '',
};

function AnnullaFatturaModal({ fattura, onClose, onDone, onRifiuto, onToast }) {
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState('');
  const conferma = async () => {
    setBusy(true);
    setErrore('');
    try {
      await fattureApi.annulla(fattura.id);
      onToast && onToast({ type: 'success', text: fattura.numero ? `Fattura ${fattura.numero} annullata` : 'Fattura annullata' });
      onDone();
    } catch (e) {
      const msg = e.response?.data?.error || 'Annullamento non riuscito. Riprova.';
      // 13/10 — un rifiuto del server torna alla finestra della fattura, che si
      // ricarica e mostra lo stato vero con il messaggio accanto.
      if (e.response && onRifiuto) { onRifiuto(msg); return; }
      setErrore(msg);
    } finally { setBusy(false); }
  };
  const righe = testoAvvisoAnnulla(fattura);
  const titolo = fattura.numero ? `Annullare la fattura ${fattura.numero}?` : 'Annullare la fattura?';
  return (
    <Modal title={titolo} onClose={onClose}>
      <div role="alert" style={{ display: 'flex', gap: 10, padding: '12px 14px', marginBottom: 20, borderRadius: 'var(--radius-base)', background: tint('var(--warn)', 12), border: `1px solid ${tint('var(--warn)', 45)}`, color: 'var(--ink)', fontSize: 14, lineHeight: 1.5 }}>
        <AlertTriangle size={18} strokeWidth={2} style={{ flexShrink: 0, marginTop: 2, color: 'var(--warn)' }} />
        <div>{righe.map((r) => <p key={r} style={{ margin: '0 0 6px' }}>{r}</p>)}</div>
      </div>
      {errore && (
        <div role="alert" style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 'var(--radius-base)', fontSize: 13, background: tint('var(--ds-danger)', 10), border: '1px solid var(--ds-danger)', color: 'var(--ds-danger)' }}>{errore}</div>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <button onClick={onClose} disabled={busy} style={{ minHeight: 44, padding: '10px 18px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: 'var(--ds-text)' }}>Torna indietro</button>
        <button onClick={conferma} disabled={busy} style={{ minHeight: 44, padding: '10px 18px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--ds-danger)', cursor: busy ? 'wait' : 'pointer', fontSize: 14, fontWeight: 500, color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Ban size={15} strokeWidth={2} /> {busy ? 'Annullamento…' : 'Annulla la fattura'}
        </button>
      </div>
    </Modal>
  );
}

function StatoBadge({ stato }) {
  const m = STATO_META[stato] || { label: stato, color: 'var(--ds-muted)' };
  return <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 99, fontSize: 12, fontWeight: 500, background: tint(m.color, 14), color: m.color }}>{m.label}</span>;
}

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', width: '100%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 28px', borderBottom: '1px solid var(--ds-border)' }}>
          <h2 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, color: 'var(--ink)' }}>{title}</h2>
          <button onClick={onClose} aria-label="Chiudi" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--ds-muted)', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>
        <div style={{ padding: '24px 28px' }}>{children}</div>
      </div>
    </div>
  );
}

function FatturaForm({ initial, clientiList, cantieriList, onSave, onCancel, saving }) {
  // DBT-311: in modifica il modulo parte dall'aliquota vera della bozza, mai dal 22% del modulo nuovo.
  const [form, setForm] = useState(() => moduloIniziale(FORM_EMPTY, initial));
  const [importBanner, setImportBanner] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inp = { width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: 'var(--ds-bg)', color: 'var(--ink)' };
  const lbl = { display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ds-muted)', marginBottom: 4 };
  const row2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 };

  // Solo display (il totale autorevole è ricalcolato server-side con Decimal).
  // DBT-298 (DEC-257): stessa regola del server, in centesimi interi e «metà in su».
  // DBT-311 (DEC-285 Q2): una bozza non toccata mostra i totali salvati.
  const { imponibile, iva, totale } = importiAnteprimaModifica(form, initial);
  const aliquotaScelta = form.aliquota_iva == null || form.aliquota_iva === '' ? '' : String(form.aliquota_iva);
  // L'aliquota salvata della bozza guida le voci del menu; per una fattura nuova nessuna.
  const aliquotaSalvata = initial && initial.id ? ('aliquota_iva' in initial ? initial.aliquota_iva : null) : undefined;

  const cantieriFiltrati = form.cliente
    ? cantieriList.filter((c) => c.cliente === form.cliente)
    : cantieriList;

  const handleClienteChange = (e) => {
    var nuovoCliente = e.target.value;
    set('cliente', nuovoCliente);
    if (form.cantiere) {
      var cantOk = cantieriList.find((c) => c.id === form.cantiere && c.cliente === nuovoCliente);
      if (!cantOk) set('cantiere', '');
    }
    if (nuovoCliente) {
      const c = clientiList.find((x) => x.id === nuovoCliente);
      if (c && (c.indirizzo || c.codice_fiscale || c.piva)) {
        setImportBanner(c);
      } else if (c) {
        setImportBanner({ ...c, _incomplete: true });
      } else {
        setImportBanner(null);
      }
    } else {
      setImportBanner(null);
    }
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(form); }}>
      <div style={row2}>
        <div><label style={lbl}>Cliente</label>
          <select style={inp} value={form.cliente} onChange={handleClienteChange}>
            <option value="">— Nessuno —</option>
            {clientiList.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select></div>
        <div><label style={lbl}>Cantiere</label>
          <select style={inp} value={form.cantiere} onChange={(e) => set('cantiere', e.target.value)}>
            <option value="">— Nessuno —</option>
            {cantieriFiltrati.map((c) => <option key={c.id} value={c.id}>{c.titolo}</option>)}
          </select></div>
      </div>
      {/* Banner import dati cliente — entrambi i bottoni chiudono il banner
          perché il backend salva lo snapshot automaticamente in Fattura.save();
          il banner serve come verifica visiva per l'utente, non come azione. */}
      {importBanner && !importBanner._incomplete && (
        <div style={{ marginBottom: 16, padding: '10px 14px', background: 'var(--chip)', border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-base)' }}>
          <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6 }}>
            Vuoi importare i dati di <strong>{importBanner.nome}</strong>?
          </div>
          <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginBottom: 8, lineHeight: 1.6 }}>
            {importBanner.indirizzo && <div>Indirizzo: {importBanner.indirizzo}</div>}
            {importBanner.piva && <div>P.IVA: {importBanner.piva}</div>}
            {importBanner.codice_fiscale && <div>CF: {importBanner.codice_fiscale}</div>}
            {importBanner.email && <div>Email: {importBanner.email}</div>}
            {importBanner.telefono && <div>Tel: {importBanner.telefono}</div>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => setImportBanner(null)}
              style={{ fontSize: 12, padding: '6px 14px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--zone-blue)', color: '#fff', cursor: 'pointer', fontWeight: 500 }}>
              Conferma
            </button>
            <button type="button" onClick={() => setImportBanner(null)}
              style={{ fontSize: 12, padding: '6px 14px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', cursor: 'pointer' }}>
              Salta
            </button>
          </div>
        </div>
      )}
      {importBanner && importBanner._incomplete && (
        <div style={{ marginBottom: 16, fontSize: 12, color: 'var(--warn)' }}>
          Dati cliente incompleti —{' '}
          <a href={`/clienti/${importBanner.id}`} target="_blank" rel="noopener noreferrer"
            style={{ color: 'var(--zone-blue)', textDecoration: 'underline' }}>
            Completa profilo cliente
          </a>
        </div>
      )}
      <div style={row2}>
        <div><label style={lbl}>Tipo fattura</label>
          <select style={inp} value={form.tipo} onChange={(e) => set('tipo', e.target.value)}>
            {Object.entries(TIPO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></div>
        <div><label style={lbl}>Metodo pagamento</label>
          <select style={inp} value={form.metodo_pagamento} onChange={(e) => set('metodo_pagamento', e.target.value)}>
            {Object.entries(METODI_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></div>
      </div>
      <div style={{ marginBottom: 16 }}><label style={lbl}>Descrizione prestazione *</label>
        <input required style={inp} value={form.descrizione} onChange={(e) => set('descrizione', e.target.value)} placeholder="Es. Lavori di ristrutturazione bagno" /></div>
      <div style={row2}>
        <div><label style={lbl}>Imponibile (€) *</label>
          <input required data-testid="fattura-imponibile" type="number" min="0" step="0.01" style={inp} value={form.imponibile} onChange={(e) => set('imponibile', e.target.value)} /></div>
        <div><label style={lbl}>Aliquota IVA</label>
          <select data-testid="fattura-aliquota" style={inp} value={aliquotaScelta} onChange={(e) => set('aliquota_iva', e.target.value)}>
            {opzioniAliquotaFattura(aliquotaSalvata).map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select></div>
      </div>
      {imponibile > 0 && (
        <div data-testid="fattura-anteprima-importi" style={{ marginBottom: 16, padding: '12px 16px', background: tint('var(--brand)', 10), borderRadius: 'var(--radius-base)', fontSize: 13 }}>
          Imponibile: <b>{fmtEur(imponibile)}</b> · IVA {aliquotaScelta === '' ? '—' : aliquotaScelta + '%'}: <b>{fmtEur(iva)}</b> · <b style={{ color: 'var(--brand)' }}>Totale: {fmtEur(totale)}</b>
        </div>
      )}
      <div style={row2}>
        <div><label style={lbl}>Data emissione</label>
          <input type="date" style={inp} value={form.data_emissione} onChange={(e) => set('data_emissione', e.target.value)} /></div>
        <div><label style={lbl}>Data scadenza</label>
          <input type="date" style={inp} value={form.data_scadenza} onChange={(e) => set('data_scadenza', e.target.value)} /></div>
      </div>
      <div style={{ marginBottom: 24 }}><label style={lbl}>Note per il cliente</label>
        <textarea style={{ ...inp, resize: 'vertical', minHeight: 70 }} value={form.note_cliente} onChange={(e) => set('note_cliente', e.target.value)} /></div>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onCancel} style={{ padding: '10px 20px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>Annulla</button>
        <button type="submit" disabled={saving} style={{ padding: '10px 24px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>{saving ? 'Salvataggio…' : 'Salva fattura'}</button>
      </div>
    </form>
  );
}

function FatturaDetail({ fattura, onClose, onDelete, onRefresh, onRicarica, onEdit, onToast }) {
  const [showInviaCliente, setShowInviaCliente] = useState(false);
  const [acting, setActing] = useState(false);
  const [pdfMsg, setPdfMsg] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [savingPdf, setSavingPdf] = useState(false);
  const [copyMsg, setCopyMsg] = useState(null); // null | 'ok' | 'err' — feedback inline "Copia"
  const [noteText, setNoteText] = useState(fattura.note_interne || '');
  const [savedNote, setSavedNote] = useState(fattura.note_interne || '');
  const [savingNote, setSavingNote] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);
  const [showSollecito, setShowSollecito] = useState(false);
  const [showAnnulla, setShowAnnulla] = useState(false);
  const [erroreAzione, setErroreAzione] = useState('');
  // GATE C.1 — traccia dell'ultimo sollecito inviato (dal backend, poi
  // aggiornata in locale dopo un invio riuscito).
  const [sollecitatoIl, setSollecitatoIl] = useState(fattura.sollecito_inviato_il || null);
  const noteBtn = { display: 'flex', alignItems: 'center', gap: 6, minHeight: 36, flexShrink: 0, padding: '6px 12px', fontSize: 13, fontWeight: 500, color: 'var(--brand)', background: 'transparent', border: '1px solid var(--brand)', borderRadius: 'var(--radius-base)', cursor: 'pointer' };

  const salvaNote = async () => {
    setSavingNote(true);
    try {
      await fattureApi.update(fattura.id, { note_interne: noteText });
      setSavedNote(noteText);
      setNoteSaved(true);
      setTimeout(() => setNoteSaved(false), 1800);
      onRefresh && onRefresh();
    } catch (e) {
      // DBT-053 (14/10) — fattura cambiata nel frattempo (409): stesso meccanismo
      // degli altri rifiuti, la finestra si ricarica e mostra lo stato vero.
      if (e.response?.status === 409) rifiuto(e, 'Salvataggio note non riuscito');
      else onToast && onToast({ type: 'error', text: 'Salvataggio note non riuscito' });
    } finally { setSavingNote(false); }
  };

  const copiaNote = async () => {
    // FE-056 — copia con fallback: funziona anche dove navigator.clipboard
    // non è disponibile (http, alcuni webview mobile).
    const ok = await copiaTesto(noteText);
    if (ok) {
      setCopyMsg('ok'); setTimeout(() => setCopyMsg(null), 1800);
    } else {
      setCopyMsg('err'); setTimeout(() => setCopyMsg(null), 3000);
    }
  };
  const lbl = { fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' };
  const val = { fontSize: 14, color: 'var(--ink)', marginTop: 2 };

  // 13/10 — dopo qualunque rifiuto del server la finestra si ricarica dalla
  // fattura vera (numero, stato, pulsanti) e il messaggio resta accanto.
  const ricarica = () => { onRefresh(); if (onRicarica) onRicarica(fattura.id); };
  const rifiuto = (e, predefinito) => {
    setErroreAzione(e.response?.data?.error || predefinito);
    ricarica();
  };
  const mancanti = mancantiPerEmettere(fattura);

  const azione = async (fn) => {
    setActing(true);
    setErroreAzione('');
    try { await fn(); onRefresh(); onClose(); }
    catch (e) { rifiuto(e, 'Operazione non riuscita. Riprova.'); }
    finally { setActing(false); }
  };

  const scaricaPdf = async () => {
    setDownloadingPdf(true);
    setPdfMsg(null);
    try {
      const resp = await fattureApi.pdf(fattura.id);
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Proforma_${(fattura.numero || 'fattura').replace(/\//g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setPdfMsg({ type: 'error', text: 'Errore nella generazione del PDF' });
    } finally { setDownloadingPdf(false); }
  };

  const salvaPdf = async () => {
    setSavingPdf(true);
    setPdfMsg(null);
    try {
      const { data } = await fattureApi.pdfSalva(fattura.id);
      const dove = data.cartella ? `cartella "${data.cartella}"` : 'archivio documenti';
      setPdfMsg({ type: 'success', text: `PDF salvato in ${dove} e in Commercialista.`, link: data.link });
    } catch (e) {
      setPdfMsg({ type: 'error', text: e.response?.data?.error || 'Errore nel salvataggio del PDF' });
    } finally { setSavingPdf(false); }
  };

  const actionBtn = (bg) => ({ flex: 1, padding: '9px 0', borderRadius: 'var(--radius-base)', border: 'none', background: bg, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 });

  return (
    <Modal title={fattura.numero ? `Fattura ${fattura.numero}` : "Fattura in bozza · n. all'invio"} onClose={onClose}>
      {/* Azioni stato */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {fattura.stato === 'bozza' && onEdit && (
          <button onClick={() => onEdit(fattura)} style={{ padding: '9px 16px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: 'var(--ds-text)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Pencil size={14} strokeWidth={2} />
            Modifica
          </button>
        )}
        {fattura.stato === 'bozza' && mancanti.length === 0 && (
          <button disabled={acting} onClick={() => { if (window.confirm('Segnare come inviata?')) azione(() => fattureApi.invia(fattura.id)); }} style={actionBtn('var(--warn)')}><Send size={15} strokeWidth={2} /> Segna come inviata</button>
        )}
        {(fattura.stato === 'inviata' || fattura.stato === 'scaduta') && (
          <button disabled={acting} onClick={() => { if (window.confirm('Segnare come pagata? Questa azione non è reversibile.')) azione(() => fattureApi.segnaPagata(fattura.id)); }} style={actionBtn('var(--ok)')}><Check size={15} strokeWidth={2} /> Segna come pagata</button>
        )}
        {(fattura.stato === 'inviata' || fattura.stato === 'scaduta') && (
          <button onClick={() => setShowSollecito(true)} style={actionBtn('var(--warn)')}><Mail size={15} strokeWidth={2} /> Sollecita cliente</button>
        )}
        {/* DBT-053 — «Elimina» solo sulle bozze; le emesse si annullano. */}
        {fattura.stato === 'bozza' && (
          <button onClick={() => { if (window.confirm('Eliminare questa bozza?')) { setErroreAzione(''); fattureApi.delete(fattura.id).then(() => { onRefresh(); onClose(); }).catch((e) => rifiuto(e, 'Eliminazione non riuscita. Riprova.')); } }} style={{ padding: '9px 16px', borderRadius: 'var(--radius-base)', border: `1px solid ${tint('var(--ds-danger)', 35)}`, background: tint('var(--ds-danger)', 12), color: 'var(--ds-danger)', cursor: 'pointer', fontSize: 13, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6 }}><Trash2 size={15} strokeWidth={2} /> Elimina</button>
        )}
        {STATI_ANNULLABILI.includes(fattura.stato) && (
          <button onClick={() => setShowAnnulla(true)} style={{ padding: '9px 16px', borderRadius: 'var(--radius-base)', border: `1px solid ${tint('var(--ds-danger)', 35)}`, background: tint('var(--ds-danger)', 12), color: 'var(--ds-danger)', cursor: 'pointer', fontSize: 13, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6 }}><Ban size={15} strokeWidth={2} /> Annulla fattura</button>
        )}
      </div>
      {mancanti.length > 0 && (
        <div role="status" style={{ display: 'flex', gap: 10, marginBottom: 16, padding: '10px 14px', borderRadius: 'var(--radius-base)', fontSize: 13, background: tint('var(--warn)', 12), border: `1px solid ${tint('var(--warn)', 45)}`, color: 'var(--ink)' }}>
          <AlertTriangle size={16} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1, color: 'var(--warn)' }} />
          <div>{testoCosaManca(mancanti)} Completa la bozza con «Modifica» prima di emetterla o di mandarla al cliente.</div>
        </div>
      )}
      {erroreAzione && (
        <div role="alert" style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 'var(--radius-base)', fontSize: 13, background: tint('var(--ds-danger)', 10), border: '1px solid var(--ds-danger)', color: 'var(--ds-danger)' }}>{erroreAzione}</div>
      )}
      {fattura.stato === 'annullata' && (
        <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 'var(--radius-base)', fontSize: 13, background: 'var(--chip)', border: '1px solid var(--ds-border)', color: 'var(--ds-muted)' }}>
          Fattura annullata: resta nell'elenco con il suo numero e non conta nei totali.
          {fattura.fic_document_id ? ' Su Fatture in Cloud resta valida: per stornarla serve una nota di credito su Fatture in Cloud.' : ''}
        </div>
      )}
      {showAnnulla && <AnnullaFatturaModal fattura={fattura} onClose={() => setShowAnnulla(false)} onDone={() => { setShowAnnulla(false); onRefresh(); onClose(); }} onRifiuto={(msg) => { setShowAnnulla(false); setErroreAzione(msg); ricarica(); }} onToast={onToast} />}

      {/* Azioni PDF proforma */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button disabled={downloadingPdf} onClick={scaricaPdf} style={{ padding: '9px 16px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', cursor: downloadingPdf ? 'wait' : 'pointer', fontSize: 13, fontWeight: 500, color: 'var(--ds-text)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <FileDown size={15} strokeWidth={2} /> {downloadingPdf ? 'Generazione…' : 'Genera PDF'}
        </button>
        <button disabled={savingPdf} onClick={salvaPdf} style={{ padding: '9px 16px', borderRadius: 'var(--radius-base)', border: '1px solid var(--brand)', background: tint('var(--brand)', 12), cursor: savingPdf ? 'wait' : 'pointer', fontSize: 13, fontWeight: 500, color: 'var(--brand)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <FolderDown size={15} strokeWidth={2} /> {savingPdf ? 'Salvataggio…' : 'Salva nel documentale'}
        </button>
        {/* DBT-053 (13/10, opzione b) — una bozza incompleta non si manda al
            cliente: il riquadro sopra dice cosa manca, come per l'emissione. */}
        {fattura.stato !== 'annullata' && mancanti.length === 0 && (
          <button onClick={() => setShowInviaCliente(true)} style={{ padding: '9px 16px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Mail size={15} strokeWidth={2} /> Invia al cliente
          </button>
        )}
      </div>
      {/* Emissione su Fatture in Cloud — SUL DETTAGLIO e non sulla lista:
          e' irreversibile, e un'azione che non si puo' disfare non sta a un
          tocco di distanza da una lista che si scorre col pollice. Qui
          l'artigiano ha appena letto cliente, importi e IVA. */}
      {fattura.stato !== 'annullata' && mancanti.length === 0 && <EmissioneFIC fattura={fattura} onRefresh={ricarica} />}
      {showInviaCliente && <InvioClienteModal documento={fattura} config={FATTURA_INVIO_CONFIG} onClose={() => setShowInviaCliente(false)} onToast={onToast} />}
      {pdfMsg && (
        <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 'var(--radius-base)', fontSize: 13,
          background: pdfMsg.type === 'error' ? tint('var(--ds-danger)', 10) : tint('var(--ok)', 12),
          border: `1px solid ${pdfMsg.type === 'error' ? 'var(--ds-danger)' : 'var(--ok)'}`,
          color: pdfMsg.type === 'error' ? 'var(--ds-danger)' : 'var(--ok)' }}>
          {pdfMsg.text}
          {pdfMsg.link && <> · <a href={pdfMsg.link} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', fontWeight: 500 }}>Apri PDF</a></>}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div><div style={lbl}>Stato</div><div style={val}><StatoBadge stato={fattura.stato} /></div></div>
        <div><div style={lbl}>Tipo</div><div style={val}>{TIPO_LABEL[fattura.tipo] || fattura.tipo}</div></div>
        <div><div style={lbl}>Cliente</div><div style={val}>{fattura.cliente_nome || fattura.cliente_nome_snapshot || '—'}</div></div>
        <div><div style={lbl}>Cantiere</div><div style={val}>{fattura.cantiere_titolo || '—'}</div></div>
        <div><div style={lbl}>Data emissione</div><div style={val}>{fmtData(fattura.data_emissione)}</div></div>
        <div><div style={lbl}>Scadenza</div><div style={{ ...val, color: fattura.is_scaduta ? 'var(--ds-danger)' : 'var(--ink)' }}>{fmtData(fattura.data_scadenza)}</div></div>
        {fattura.data_pagamento && <div><div style={lbl}>Data pagamento</div><div style={val}>{fmtData(fattura.data_pagamento)}</div></div>}
        <div><div style={lbl}>Metodo pagamento</div><div style={val}>{METODI_LABEL[fattura.metodo_pagamento] || '—'}</div></div>
      </div>

      {/* Righe */}
      {fattura.righe && fattura.righe.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ ...lbl, marginBottom: 8 }}>Voci fattura</div>
          {/* scroll-x locale: 5 colonne sfondano a 375px dentro il modale (mai scroll globale) */}
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ width: '100%', minWidth: 420, borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: 'var(--chip)' }}>
              {['Descrizione', 'Q.tà', 'Prezzo', 'IVA', 'Totale'].map((h) => (
                <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 500, color: 'var(--ds-muted)', fontSize: 11 }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {fattura.righe.map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--ds-border)' }}>
                  <td style={{ padding: '8px 10px' }}>{r.descrizione}</td>
                  <td style={{ padding: '8px 10px' }}>{r.quantita} {r.unita_misura}</td>
                  <td style={{ padding: '8px 10px' }}>{fmtEur(r.prezzo_unitario)}</td>
                  <td style={{ padding: '8px 10px' }}>{r.aliquota_iva}%</td>
                  <td style={{ padding: '8px 10px', fontWeight: 500 }}>{fmtEur(r.totale_riga)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Totali */}
      <div style={{ background: 'var(--chip)', borderRadius: 'var(--radius-base)', padding: '14px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 14, color: 'var(--ds-muted)' }}>
          <span>Imponibile</span><span>{fmtEur(fattura.imponibile)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 14, color: 'var(--ds-muted)' }}>
          <span>IVA</span><span>{fmtEur(fattura.iva_totale)}</span>
        </div>
        {parseFloat(fattura.ritenuta_acconto) > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 14, color: 'var(--ds-danger)' }}>
            <span>Ritenuta d'acconto</span><span>- {fmtEur(fattura.ritenuta_acconto)}</span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 500, fontSize: 18, color: 'var(--ink)', borderTop: '1px solid var(--ds-border)', paddingTop: 8, marginTop: 4 }}>
          <span>Totale</span><span>{fmtEur(fattura.totale)}</span>
        </div>
      </div>

      {fattura.note_cliente && (
        <div style={{ marginTop: 16, padding: 12, background: 'var(--chip)', borderRadius: 'var(--radius-base)' }}>
          <div style={lbl}>Note cliente</div>
          <div style={{ fontSize: 14, color: 'var(--ink)', marginTop: 4, whiteSpace: 'pre-wrap' }}>{fattura.note_cliente}</div>
        </div>
      )}

      {fattura.note_interne && (
        <div style={{ marginTop: 16, padding: 12, background: 'var(--chip)', borderRadius: 'var(--radius-base)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <div>
              <div style={lbl}>Note interne</div>
              <div style={{ fontSize: 12, color: 'var(--ds-muted)', marginTop: 2 }}>
                Bozza di sollecito — modificala e inviala al cliente
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {copyMsg === 'err' && (
                <span style={{ fontSize: 12, color: 'var(--ds-danger)' }}>Copia non riuscita</span>
              )}
              {noteText !== savedNote && (
                <button onClick={salvaNote} disabled={savingNote} style={noteBtn}>
                  {noteSaved ? <><Check size={14} /> Salvato</> : (savingNote ? 'Salvo…' : 'Salva')}
                </button>
              )}
              <button
                onClick={copiaNote}
                style={{ ...noteBtn, color: copyMsg === 'ok' ? 'white' : 'var(--brand)', background: copyMsg === 'ok' ? 'var(--brand)' : 'transparent', transition: 'background .15s, color .15s' }}
              >
                {copyMsg === 'ok' ? <><Check size={14} /> Copiato</> : <><Copy size={14} /> Copia</>}
              </button>
              {/* GATE C.1 — il bottone DICE che un sollecito e' gia' partito,
                  prima di ripartire. Non blocca (un secondo sollecito e'
                  legittimo): rende la scelta informata invece che al buio. */}
              <button onClick={() => setShowSollecito(true)} style={{ ...noteBtn, color: 'white', background: 'var(--brand)' }}>
                <Mail size={14} /> {sollecitatoIl ? `Sollecita di nuovo (ultimo: ${fmtQuando(sollecitatoIl)})` : 'Invia email'}
              </button>
            </div>
          </div>
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            rows={8}
            style={{ width: '100%', marginTop: 8, fontSize: 14, color: 'var(--ink)', background: 'var(--ds-bg)', border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-base)', padding: 10, fontFamily: 'inherit', lineHeight: 1.5, resize: 'vertical', boxSizing: 'border-box' }}
          />
        </div>
      )}
      {showSollecito && (
        <InviaSollecitoModal
          fattura={fattura}
          corpoIniziale={noteText || bozzaSollecitoDefault(fattura)}
          sollecitatoIl={sollecitatoIl}
          onClose={() => setShowSollecito(false)}
          onSent={(corpo) => {
            setNoteText(corpo); setSavedNote(corpo); setShowSollecito(false);
            // GATE C.1 — aggiornamento locale: onRefresh ricarica la LISTA, non
            // la prop `fattura` di questa scheda. Senza, il bottone continuerebbe
            // a dire "Invia email" subito dopo un invio riuscito.
            setSollecitatoIl(new Date().toISOString());
            onRefresh && onRefresh();
          }}
          onToast={onToast}
        />
      )}
    </Modal>
  );
}

// Modale invio sollecito al cliente. Riusa il sender condiviso (backend
// invia_email_come_artigiano, lo stesso di fatture/preventivi); qui il
// contenuto e' il TESTO delle note interne, personalizzabile prima dell'invio.
function InviaSollecitoModal({ fattura, corpoIniziale, sollecitatoIl, onClose, onSent, onToast }) {
  const emailAnagrafica = fattura.cliente_email || '';
  const [email, setEmail] = useState(emailAnagrafica);
  const [oggetto, setOggetto] = useState(`Sollecito pagamento — Fattura ${fattura.numero || ''}`.trim());
  const [corpo, setCorpo] = useState(corpoIniziale || '');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const inp = { width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: 'var(--ds-bg)', color: 'var(--ink)' };
  const lbl = { fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4, display: 'block' };

  const invia = async () => {
    if (!email.trim()) { setError('Inserisci un indirizzo email per inviare.'); return; }
    if (!corpo.trim()) { setError('Il testo del sollecito è vuoto.'); return; }
    setSending(true); setError('');
    try {
      const { data } = await fattureApi.inviaSollecito(fattura.id, { email: email.trim(), oggetto: oggetto.trim(), corpo });
      onToast && onToast({ type: 'success', text: `Sollecito inviato a ${data.destinatario} ✅` });
      onSent && onSent(corpo);
    } catch (e) {
      setError(e.response?.data?.error || 'Invio non riuscito. Riprova.');
    } finally { setSending(false); }
  };

  return (
    <Modal title="Invia sollecito al cliente" onClose={onClose}>
      {/* GATE C.1 — l'avviso sta PRIMA del testo, non accanto al pulsante:
          "l'ho gia' mandato?" e' la domanda che decide se rimandarlo, e va
          risposta all'apertura. Avviso, non blocco: un secondo sollecito e'
          legittimo, ma dev'essere una scelta e non una svista. */}
      {sollecitatoIl && (
        <div style={{
          display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 14, padding: '10px 12px',
          fontSize: 13, lineHeight: 1.45, color: 'var(--ink)',
          background: tint('var(--warn)', 12), border: '1px solid var(--warn)',
          borderRadius: 'var(--radius-base)',
        }}>
          <AlertTriangle size={16} style={{ color: 'var(--warn)', flexShrink: 0, marginTop: 1 }} />
          <span>
            Un sollecito per questa fattura è già stato inviato il{' '}
            <strong>{fmtQuando(sollecitatoIl, { conOra: true })}</strong>. Se procedi, il cliente ne
            riceverà un secondo.
          </span>
        </div>
      )}
      {!emailAnagrafica && (
        <div style={{ fontSize: 13, color: 'var(--ds-muted)', marginBottom: 12 }}>
          {fattura.cliente_nome || 'Il cliente'} non ha un'email in anagrafica. Inseriscila qui sotto per inviare.
        </div>
      )}
      <div style={{ marginBottom: 14 }}>
        <label style={lbl}>Destinatario</label>
        <input type="email" style={inp} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@cliente.it" />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={lbl}>Oggetto</label>
        <input style={inp} value={oggetto} onChange={(e) => setOggetto(e.target.value)} />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={lbl}>Testo dell'email</label>
        <textarea rows={10} style={{ ...inp, resize: 'vertical', lineHeight: 1.5 }} value={corpo} onChange={(e) => setCorpo(e.target.value)} />
      </div>
      {error && <div style={{ fontSize: 13, color: 'var(--ds-danger)', marginBottom: 12 }}>{error}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button onClick={onClose} disabled={sending} style={{ padding: '9px 16px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-muted)', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>Annulla</button>
        <button onClick={invia} disabled={sending} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: 'white', fontSize: 14, fontWeight: 500, cursor: sending ? 'default' : 'pointer', opacity: sending ? 0.7 : 1 }}>
          <Mail size={15} /> {sending ? 'Invio…' : 'Invia sollecito'}
        </button>
      </div>
    </Modal>
  );
}

// Coda ranking Fatture: conteggio fatture. Dominio locale — il plurale
// "fattura/fatture" vive qui, mai nel componente condiviso.
function renderCodaFatture(row, { isMobile }) {
  if (isMobile) {
    return (
      <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{row.count}</div>
        <div style={{ fontSize: 11, color: 'var(--ds-muted)' }}>{row.count === 1 ? 'fattura' : 'fatture'}</div>
      </div>
    );
  }
  return (
    <td style={{ padding: '11px 14px', fontSize: 11, color: 'var(--ds-muted)', fontWeight: 500 }}>{row.count}</td>
  );
}

export default function Fatture() {
  const isMobile = useIsMobile();
  const { isCards, mode, setMode } = useListMode('fatture', isMobile);
  const [lista, setLista] = useState([]);
  const [kpi, setKpi] = useState(null);
  const [clientiList, setClientiList] = useState([]);
  const [cantieriList, setCantieriList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Deep-link da Home: /fatture?stato=scaduta pre-applica il filtro (link condivisibile, back del browser).
  const [filtroStato, setFiltroStato] = useState(() => {
    const s = new URLSearchParams(window.location.search).get('stato');
    return STATI_LIST.includes(s) ? s : '';
  });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dettaglio, setDettaglio] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [showInvia, setShowInvia] = useState(false);
  const [showInviaClienti, setShowInviaClienti] = useState(false);
  const [inviando, setInviando] = useState(false);
  const [toast, setToast] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const emailCommercialista = user?.tenant?.email_commercialista || '';

  const carica = useCallback(async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const params = {};
      if (search) params.search = search;
      if (filtroStato) params.stato = filtroStato;
      const [listRes, kpiRes] = await Promise.all([fattureApi.list(params), fattureApi.kpi()]);
      setLista(listRes.data.results ?? listRes.data);
      setKpi(kpiRes.data);
    } catch {} finally { setLoading(false); }
  }, [search, filtroStato]);

  const toggleSel = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const inviaCommercialista = async () => {
    setInviando(true);
    try {
      const { data } = await fattureApi.inviaCommercialista([...selected]);
      const base = `${data.inviate} ${data.inviate === 1 ? 'fattura inviata' : 'fatture inviate'} a ${data.destinatario}`;
      setToast({ type: 'success', text: data.warning ? `${base}. ${data.warning}` : `${base} ✅` });
      setShowInvia(false);
      setSelected(new Set());
    } catch (e) {
      setToast({ type: 'error', text: e.response?.data?.error || 'Errore nell\'invio al commercialista' });
    } finally { setInviando(false); }
  };

  useEffect(() => { carica(); }, [carica]);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(null), 6000); return () => clearTimeout(t); }, [toast]);
  useEffect(() => {
    clientiApi.list().then((r) => setClientiList(r.data.results ?? r.data)).catch(() => {});
    cantieriApi.list().then((r) => setCantieriList(r.data.results ?? r.data)).catch(() => {});
  }, []);

  // Apri form automaticamente se navigazione da Home/sidebar (+Nuova fattura)
  useEffect(() => {
    if (location.pathname === '/fatture/nuovo') { setEditing(null); setShowForm(true); }
  }, []); // eslint-disable-line

  // Deep-link da centro notifiche: /fatture?apri={id} apre la scheda fattura
  useEffect(() => {
    const apriId = new URLSearchParams(location.search).get('apri');
    if (apriId) fattureApi.get(apriId).then((r) => setDettaglio(r.data)).catch(() => {});
  }, [location.search]);

  // 13/10 — ricarica la fattura aperta dal server (stato vero dopo un rifiuto).
  const ricaricaDettaglio = async (id) => {
    try { const r = await fattureApi.get(id); setDettaglio(r.data); } catch { /* resta quella mostrata */ }
  };

  const apriDettaglio = async (f) => {
    setLoadingDetail(true);
    try {
      const r = await fattureApi.get(f.id);
      setDettaglio(r.data);
    } catch { setDettaglio(f); }
    finally { setLoadingDetail(false); }
  };

  const salva = async (form) => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.cliente) delete payload.cliente;
      if (!payload.cantiere) delete payload.cantiere;
      if (!payload.data_scadenza) delete payload.data_scadenza;
      if (editing) {
        // DBT-311: imponibile e aliquota invariati non si rimandano.
        await fattureApi.update(editing.id, senzaImportiInvariati(payload, editing));
      } else {
        await fattureApi.create(payload);
      }
      setShowForm(false); setEditing(null); carica();
    } catch (e) { alert(messaggioErroreSalvataggio(e)); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ padding: '28px 20px 28px 32px', width: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 600, color: 'var(--ink)' }}>Fatture</h1>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--ds-muted)' }}>Gestione fatturazione e pagamenti</p>
        </div>
        <button onClick={() => setShowForm(true)} style={{ padding: '11px 22px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500, boxShadow: '0 4px 14px rgba(46,160,132,0.35)' }}>+ Nuova fattura</button>
      </div>

      {/* KPI */}
      {kpi && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14, marginBottom: 28 }}>
          {[
            { label: 'Fatturato ' + (kpi.anno || ''), value: fmtEur(kpi.emesso_ytd), color: 'var(--brand)' },
            { label: 'Incassato ' + (kpi.anno || ''), value: fmtEur(kpi.totale_fatturato), color: 'var(--ok)' },
            { label: 'Da incassare', value: fmtEur(kpi.da_incassare), color: 'var(--zone-amber)',
              detail: (kpi.da_incassare_scaduta || 0) > 0 ? `di cui scadute ${fmtEur(kpi.da_incassare_scaduta)}` : null,
              hint: 'Insoluto totale, anche di anni precedenti' },
            { label: 'Bozze', value: kpi.bozze, color: 'var(--zone-slate)' },
          ].map((s) => (
            <div key={s.label} style={{ background: 'var(--chip)', borderRadius: 'var(--radius-card)', padding: '20px 24px', border: '1px solid var(--ds-border)', borderTop: `3px solid ${s.color}`, boxShadow: CARD_SHADOW }}>
              <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: typeof s.value === 'string' ? 18 : 24, fontWeight: 700, color: 'var(--ink)', marginTop: 4 }}>{s.value}</div>
              {s.detail && <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--warn)', marginTop: 4 }}>{s.detail}</div>}
              {s.hint && <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', marginTop: 4, lineHeight: 1.3, textTransform: 'none', letterSpacing: 0 }}>{s.hint}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Filtri */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--ds-muted)', display: 'flex' }}><Search size={16} strokeWidth={2} /></span>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca numero, cliente…"
            style={{ width: '100%', padding: '10px 12px 10px 36px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: 'var(--ds-bg)', color: 'var(--ink)' }} />
        </div>
        <button onClick={() => setFiltroStato('')} style={{
          padding: '10px 16px', borderRadius: 'var(--radius-base)', fontSize: 13, fontWeight: 500, cursor: 'pointer',
          background: !filtroStato ? 'var(--brand)' : 'var(--ds-bg)',
          color: !filtroStato ? '#fff' : 'var(--ds-text)',
          border: !filtroStato ? '1px solid var(--brand)' : '1px solid var(--ds-border)',
        }}>Tutti</button>
        <select value={filtroStato} onChange={(e) => setFiltroStato(e.target.value)}
          style={{ padding: '10px 14px', borderRadius: 'var(--radius-base)', fontSize: 13, fontWeight: 500, cursor: 'pointer', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', outline: 'none', fontFamily: 'inherit' }}>
          <option value="">Filtra stato…</option>
          {STATI_LIST.map((s) => <option key={s} value={s}>{STATO_META[s].label}</option>)}
        </select>
        {!isMobile && (
          <div style={{ marginLeft: 'auto' }}>
            <ListModeToggle mode={mode} setMode={setMode} />
          </div>
        )}
      </div>

      {/* Toolbar selezione multipla */}
      {selected.size > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16, padding: '12px 18px', borderRadius: 'var(--radius-card)', background: 'color-mix(in srgb, var(--brand) 10%, var(--ds-bg))', border: '1px solid var(--brand)', boxShadow: CARD_SHADOW }}>
          <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>{selected.size} {selected.size === 1 ? 'fattura selezionata' : 'fatture selezionate'}</span>
          <button onClick={() => setShowInvia(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
            <Send size={15} strokeWidth={2} /> Invia al commercialista ({selected.size})
          </button>
          <button onClick={() => setShowInviaClienti(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 'var(--radius-base)', border: '1px solid var(--brand)', background: tint('var(--brand)', 12), color: 'var(--brand)', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
            <Mail size={15} strokeWidth={2} /> Invia ai clienti ({selected.size})
          </button>
          <button onClick={() => setSelected(new Set())} style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4, padding: '8px 12px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-muted)', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
            <X size={14} strokeWidth={2} /> Deseleziona
          </button>
        </div>
      )}

      {/* Lista + Ranking */}
      {(() => {
        // Ranking dalla FONTE UNICA backend (kpi.top_clienti = imponibile emesso YTD).
        // Prima era aggregato su `lista` (paginata a 20, somma `totale` con IVA, tutti
        // gli stati incl. bozza/annullata) → quarta definizione divergente. Ora eredita
        // la stessa definizione "emesso" della card "Fatturato".
        const rankingFatture = (kpi?.top_clienti || []).map((r, i) => ({
          pos: i + 1, cliente: r.cliente, totale: r.totale, count: r.count,
        }));
        const listaBody = (
          <>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--ds-muted)' }}>Caricamento…</div>
            ) : lista.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 0', background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', boxShadow: CARD_SHADOW }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14, color: 'var(--ds-muted)' }}><Receipt size={44} strokeWidth={1.5} /></div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, color: 'var(--ink)', marginBottom: 8 }}>Nessuna fattura trovata</div>
                  <div style={{ fontSize: 14, color: 'var(--ds-muted)', marginBottom: 24 }}>{search || filtroStato ? 'Prova a cambiare i filtri' : 'Crea la tua prima fattura'}</div>
                  {!search && !filtroStato && <button onClick={() => setShowForm(true)} style={{ padding: '11px 24px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>+ Nuova fattura</button>}
                </div>
              ) : isCards ? (
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gap: isMobile ? 10 : 14 }}>
                  {lista.map((f) => (
                    <DataCard
                      key={f.id}
                      title={f.cliente_nome || f.cliente_nome_snapshot || '—'}
                      subtitle={<><span style={stileAnnullata(f) || undefined}>{f.numero || 'Bozza'}</span> · {fmtData(f.data_emissione)}</>}
                      onClick={() => apriDettaglio(f)}
                      badge={<StatoBadge stato={f.stato} />}
                      meta={[
                        { label: 'Totale', value: <span style={stileAnnullata(f) || undefined}>{fmtEur(f.totale)}</span> },
                        { label: 'Scadenza', value: <span style={{ color: f.is_scaduta ? 'var(--ds-danger)' : 'var(--ink)' }}>{fmtData(f.data_scadenza)}</span> },
                        { label: 'Tipo', value: TIPO_LABEL[f.tipo] || f.tipo },
                        // GATE C.1 — solo se c'e': una riga "Sollecitata —" su ogni
                        // fattura mai sollecitata sarebbe rumore su una card gia' densa.
                        ...(f.sollecito_inviato_il
                          ? [{ label: 'Sollecitata', value: fmtQuando(f.sollecito_inviato_il) }]
                          : []),
                        // Fatture in Cloud: SOLO lo stato, mai l'azione. Serve a
                        // scorrere («quali devo ancora emettere?»); il bottone sta
                        // nel dettaglio, dove si decide. E l'etichetta dice cosa
                        // abbiamo fatto NOI — «Inviata a FIC», non «esiste su FIC»:
                        // il campo non sa se l'artigiano l'ha poi cancellata dal
                        // pannello (R58, il nome e' un'affermazione).
                        { label: 'Fatture in Cloud', value: etichettaListaFIC(f.fic_stato_invio).testo },
                      ]}
                    />
                  ))}
                </div>
              ) : (
                <div style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', boxShadow: CARD_SHADOW, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--ds-border)' }}>
                    <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}>Elenco Fatture</span>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr style={{ background: 'var(--chip)', borderBottom: '2px solid var(--ds-border)' }}>
                      <th style={{ padding: '12px 8px 12px 16px', width: 32 }}>
                        <input type="checkbox" aria-label="Seleziona tutte"
                          checked={lista.length > 0 && selected.size === lista.length}
                          ref={(el) => { if (el) el.indeterminate = selected.size > 0 && selected.size < lista.length; }}
                          onChange={(e) => setSelected(e.target.checked ? new Set(lista.map((f) => f.id)) : new Set())}
                          style={{ width: 16, height: 16, accentColor: 'var(--brand)', cursor: 'pointer' }} />
                      </th>
                      {['Numero', 'Cliente', 'Cantiere', 'Tipo', 'Imponibile', 'IVA', 'Totale', 'Emissione', 'Scadenza', 'Stato'].map((h) => (
                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {lista.map((f, i) => (
                        <tr key={f.id} onClick={() => apriDettaglio(f)} style={{ borderTop: '1px solid var(--ds-border)', cursor: 'pointer', background: selected.has(f.id) ? 'color-mix(in srgb, var(--brand) 10%, var(--ds-bg))' : ROW_BG(i) }}
                          onMouseEnter={(e) => { if (!selected.has(f.id)) e.currentTarget.style.background = ROW_HOVER; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = selected.has(f.id) ? 'color-mix(in srgb, var(--brand) 10%, var(--ds-bg))' : ROW_BG(i); }}>
                          <td onClick={(e) => e.stopPropagation()} style={{ padding: '13px 8px 13px 16px' }}>
                            <input type="checkbox" aria-label={`Seleziona ${f.numero || 'bozza'}`} checked={selected.has(f.id)} onChange={() => toggleSel(f.id)}
                              style={{ width: 16, height: 16, accentColor: 'var(--brand)', cursor: 'pointer' }} />
                          </td>
                          <td style={{ padding: '13px 16px', fontWeight: 500, fontSize: 14, color: 'var(--ink)', ...stileAnnullata(f) }}>{f.numero || <span style={{ fontStyle: 'italic', fontWeight: 400, color: 'var(--ds-muted)' }}>{NUM_BOZZA}</span>}</td>
                          <td style={{ padding: '13px 16px', fontSize: 11, color: 'var(--ds-muted)' }}>{f.cliente_nome || <span style={{ color: 'var(--ds-border)' }}>—</span>}</td>
                          <td style={{ padding: '13px 16px', fontSize: 11, color: 'var(--ds-muted)' }}>{f.cantiere_titolo || <span style={{ color: 'var(--ds-border)' }}>—</span>}</td>
                          <td style={{ padding: '13px 16px', fontSize: 11, color: 'var(--ds-muted)' }}>{TIPO_LABEL[f.tipo] || f.tipo}</td>
                          <td style={{ padding: '13px 16px', fontSize: 11, color: 'var(--ds-muted)', whiteSpace: 'nowrap', ...stileAnnullata(f) }}>{fmtEur(f.imponibile)}</td>
                          <td style={{ padding: '13px 16px', fontSize: 11, color: 'var(--ds-muted)', whiteSpace: 'nowrap', ...stileAnnullata(f) }}>{fmtEur(f.iva_totale)}</td>
                          <td style={{ padding: '13px 16px', fontSize: 11, fontWeight: 500, color: 'var(--ink)', whiteSpace: 'nowrap', ...stileAnnullata(f) }}>{fmtEur(f.totale)}</td>
                          <td style={{ padding: '13px 16px', fontSize: 11, color: 'var(--ds-muted)', whiteSpace: 'nowrap' }}>{fmtData(f.data_emissione)}</td>
                          <td style={{ padding: '13px 16px', fontSize: 11, whiteSpace: 'nowrap', color: f.is_scaduta ? 'var(--ds-danger)' : 'var(--ds-muted)' }}>{fmtData(f.data_scadenza)}</td>
                          {/* GATE C.1 — la conferma che NON scade dopo sei secondi e non
                              dipende da dove sta guardando l'occhio. Sotto il badge di stato
                              perche' "l'ho gia' sollecitata?" e' una domanda sullo stato della
                              riscossione, e si legge insieme a scadenza e stato. */}
                          <td style={{ padding: '13px 16px' }}>
                            <StatoBadge stato={f.stato} />
                            {f.sollecito_inviato_il && (
                              <div style={{ fontSize: 9, color: 'var(--ds-muted)', marginTop: 4, whiteSpace: 'nowrap' }}>
                                Sollecitata il {fmtQuando(f.sollecito_inviato_il)}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
            )}
          </>
        );
        return isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {listaBody}
            <RankingTable
              title="Top Clienti per Fatturato"
              colNome="CLIENTE"
              colValore="IMPORTO"
              colCoda="FATTURE"
              rows={rankingFatture}
              getNome={(row) => row.cliente}
              getSub={(row) => fmtEur(row.totale)}
              renderCoda={renderCodaFatture}
            />
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '5fr 2fr', gap: 24, alignItems: 'start' }}>
            <div>{listaBody}</div>
            <RankingTable
              title="Top Clienti per Fatturato"
              colNome="CLIENTE"
              colValore="IMPORTO"
              colCoda="FATTURE"
              rows={rankingFatture}
              getNome={(row) => row.cliente}
              getSub={(row) => fmtEur(row.totale)}
              renderCoda={renderCodaFatture}
            />
          </div>
        );
      })()}

      {showForm && (
        <Modal title={editing ? (editing.numero ? `Modifica ${editing.numero}` : 'Modifica bozza') : 'Nuova fattura'} onClose={() => { setShowForm(false); setEditing(null); }}>
          <FatturaForm initial={editing || {}} clientiList={clientiList} cantieriList={cantieriList} onSave={salva} onCancel={() => { setShowForm(false); setEditing(null); }} saving={saving} />
        </Modal>
      )}
      {loadingDetail && <div style={{ position: 'fixed', inset: 0, zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.2)' }}><div style={{ background: 'var(--ds-bg)', padding: 24, borderRadius: 'var(--radius-card)' }}>Caricamento…</div></div>}
      {dettaglio && <FatturaDetail fattura={dettaglio} onClose={() => setDettaglio(null)} onRefresh={carica} onRicarica={ricaricaDettaglio} onEdit={(f) => { setDettaglio(null); setEditing(f); setShowForm(true); }} onToast={setToast} />}

      {/* Modal conferma invio commercialista */}
      {showInvia && (() => {
        const selFatture = lista.filter((f) => selected.has(f.id));
        return (
          <Modal title="Invia al commercialista" onClose={() => !inviando && setShowInvia(false)}>
            {!emailCommercialista ? (
              <div>
                <div style={{ padding: '14px 16px', borderRadius: 'var(--radius-base)', background: tint('var(--warn)', 12), border: '1px solid var(--warn)', color: 'var(--ink)', fontSize: 14, marginBottom: 20 }}>
                  Email del commercialista non configurata. Aggiungila in <strong>Impostazioni → Dati azienda</strong> per inviare le fatture.
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button onClick={() => setShowInvia(false)} style={{ padding: '10px 18px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>Annulla</button>
                  <button onClick={() => navigate('/impostazioni')} style={{ padding: '10px 18px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>Vai a Impostazioni</button>
                </div>
              </div>
            ) : (
              <div>
                <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--ds-text)' }}>
                  {selFatture.length} {selFatture.length === 1 ? 'proforma verrà inviata' : 'proforma verranno inviate'} a <strong>{emailCommercialista}</strong>.
                </p>
                <div style={{ border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-base)', overflow: 'hidden', marginBottom: 20 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead><tr style={{ background: 'var(--chip)' }}>
                      {['Numero', 'Cliente', 'Importo'].map((h) => (
                        <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 500, color: 'var(--ds-muted)', fontSize: 11 }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {selFatture.map((f) => (
                        <tr key={f.id} style={{ borderTop: '1px solid var(--ds-border)' }}>
                          <td style={{ padding: '9px 12px', color: 'var(--ink)', fontWeight: 500 }}>{f.numero || <span style={{ fontStyle: 'italic', fontWeight: 400, color: 'var(--ds-muted)' }}>{NUM_BOZZA}</span>}</td>
                          <td style={{ padding: '9px 12px', color: 'var(--ds-muted)' }}>{f.cliente_nome || '—'}</td>
                          <td style={{ padding: '9px 12px', color: 'var(--ink)', fontWeight: 500 }}>{fmtEur(f.totale)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button disabled={inviando} onClick={() => setShowInvia(false)} style={{ padding: '10px 18px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>Annulla</button>
                  <button disabled={inviando} onClick={inviaCommercialista} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 20px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: inviando ? 'wait' : 'pointer', fontSize: 14, fontWeight: 500 }}>
                    <Send size={15} strokeWidth={2} /> {inviando ? 'Invio…' : 'Invia'}
                  </button>
                </div>
              </div>
            )}
          </Modal>
        );
      })()}

      {/* Modal invio multiplo ai clienti */}
      {showInviaClienti && (
        <InvioClientiModal
          documenti={lista.filter((f) => selected.has(f.id))}
          config={FATTURA_INVIO_CONFIG}
          onClose={() => setShowInviaClienti(false)}
          onToast={setToast}
          onDone={() => { setSelected(new Set()); carica(); }}
        />
      )}

      {/* Toast esito invio */}
      {toast && (
        <div onClick={() => setToast(null)} style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000, maxWidth: 420, padding: '14px 18px', borderRadius: 'var(--radius-card)', cursor: 'pointer',
          background: toast.type === 'error' ? tint('var(--ds-danger)', 14) : tint('var(--ok)', 14),
          border: `1px solid ${toast.type === 'error' ? 'var(--ds-danger)' : 'var(--ok)'}`,
          color: toast.type === 'error' ? 'var(--ds-danger)' : 'var(--ok)', fontSize: 14, fontWeight: 500, boxShadow: CARD_SHADOW }}>
          {toast.text}
        </div>
      )}
    </div>
  );
}
